import { useState, useRef, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/components/feature/Toast';
import { useAuth } from '@/context/AuthContext';

interface ProductoFormData {
  nombre: string;
  tipo_producto: string;
  descripcion: string;
  laboratorio: string;
  principio_activo: string;
  fecha_vencimiento: string;
  precio_compra: string;
  precio: string;
  stock: string;
  stock_minimo: string;
  ubicacion: string;
  proveedor: string;
  numero_recibo: string;
}

export interface EditableProducto {
  id: string;
  nombre: string;
  tipo_producto?: string | null;
  descripcion?: string | null;
  laboratorio?: string | null;
  principio_activo?: string | null;
  fecha_vencimiento?: string | null;
  precio_compra?: number | null;
  precio: number;
  stock: number;
  stock_minimo?: number | null;
  ubicacion?: string | null;
  proveedor?: string | null;
  numero_recibo?: string | null;
  imagen?: string | null;
}

interface ProductFormModalProps {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  editingProduct?: EditableProducto | null;
}

const EMPTY_FORM: ProductoFormData = {
  nombre: '',
  tipo_producto: '',
  descripcion: '',
  laboratorio: '',
  principio_activo: '',
  fecha_vencimiento: '',
  precio_compra: '',
  precio: '',
  stock: '',
  stock_minimo: '5',
  ubicacion: '',
  proveedor: '',
  numero_recibo: '',
};

export default function ProductFormModal({ open, onClose, onSaved, editingProduct }: ProductFormModalProps) {
  const { addToast } = useToast();
  const { user } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [formData, setFormData] = useState<ProductoFormData>(EMPTY_FORM);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (editingProduct) {
      setFormData({
        nombre: editingProduct.nombre || '',
        tipo_producto: editingProduct.tipo_producto || '',
        descripcion: editingProduct.descripcion || '',
        laboratorio: editingProduct.laboratorio || '',
        principio_activo: editingProduct.principio_activo || '',
        fecha_vencimiento: editingProduct.fecha_vencimiento ? String(editingProduct.fecha_vencimiento).split('T')[0] : '',
        precio_compra: editingProduct.precio_compra != null ? editingProduct.precio_compra.toString() : '',
        precio: editingProduct.precio != null ? editingProduct.precio.toString() : '',
        stock: editingProduct.stock != null ? editingProduct.stock.toString() : '',
        stock_minimo: editingProduct.stock_minimo != null ? editingProduct.stock_minimo.toString() : '5',
        ubicacion: editingProduct.ubicacion || '',
        proveedor: editingProduct.proveedor || '',
        numero_recibo: editingProduct.numero_recibo || '',
      });
      setImagePreview(editingProduct.imagen || '');
      setImageFile(null);
    } else {
      setFormData(EMPTY_FORM);
      setImageFile(null);
      setImagePreview('');
    }
    setErrors({});
  }, [editingProduct, open]);

  const handleChange = (field: keyof ProductoFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      addToast('El archivo debe ser una imagen', 'error');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      addToast('La imagen no debe superar 5MB', 'error');
      return;
    }
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.nombre.trim()) newErrors.nombre = 'El nombre es requerido';
    if (!formData.tipo_producto.trim()) newErrors.tipo_producto = 'El tipo de producto es requerido';
    if (!formData.fecha_vencimiento) newErrors.fecha_vencimiento = 'La fecha de vencimiento es requerida';

    const precio = parseFloat(formData.precio);
    if (formData.precio === '' || Number.isNaN(precio) || precio < 0) {
      newErrors.precio = 'Ingrese un precio de venta válido';
    }

    if (formData.precio_compra !== '') {
      const precioCompra = parseFloat(formData.precio_compra);
      if (Number.isNaN(precioCompra) || precioCompra < 0) {
        newErrors.precio_compra = 'Ingrese un precio de compra válido';
      }
    }

    const stock = parseInt(formData.stock, 10);
    if (formData.stock === '' || Number.isNaN(stock) || stock < 0) {
      newErrors.stock = 'Ingrese un stock válido';
    }

    const stockMinimo = parseInt(formData.stock_minimo, 10);
    if (formData.stock_minimo === '' || Number.isNaN(stockMinimo) || stockMinimo < 0) {
      newErrors.stock_minimo = 'Ingrese un stock mínimo válido';
    }

    if (!formData.numero_recibo.trim()) {
      newErrors.numero_recibo = 'El número de recibo / factura es requerido';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    try {
      let imagenUrl = editingProduct?.imagen || '';

      if (imageFile) {
        const fileExt = imageFile.name.split('.').pop();
        const fileName = `productos/${Date.now()}-${Math.random().toString(36).substring(2, 8)}.${fileExt}`;
        const { data: uploadData, error: uploadError } = await supabase.storage
          .from('productos')
          .upload(fileName, imageFile);

        if (uploadError) {
          console.error('Error subiendo imagen:', uploadError);
          addToast('No se pudo subir la imagen. Verifica el almacenamiento.', 'error');
        } else if (uploadData) {
          const { data: urlData } = supabase.storage.from('productos').getPublicUrl(uploadData.path);
          imagenUrl = urlData.publicUrl;
        }
      }

      const stockNuevo = parseInt(formData.stock, 10) || 0;

      const productoPayload: Record<string, unknown> = {
        nombre: formData.nombre.trim(),
        tipo_producto: formData.tipo_producto.trim(),
        descripcion: formData.descripcion.trim() || null,
        laboratorio: formData.laboratorio.trim() || null,
        principio_activo: formData.principio_activo.trim() || null,
        fecha_vencimiento: formData.fecha_vencimiento || null,
        precio_compra: formData.precio_compra ? parseFloat(formData.precio_compra) : 0,
        precio: parseFloat(formData.precio),
        stock: stockNuevo,
        stock_minimo: parseInt(formData.stock_minimo, 10) || 0,
        ubicacion: formData.ubicacion.trim() || null,
        proveedor: formData.proveedor.trim() || null,
        numero_recibo: formData.numero_recibo.trim(),
        imagen: imagenUrl,
      };

      if (editingProduct) {
        const { error } = await supabase
          .from('productos')
          .update({ ...productoPayload, updated_at: new Date().toISOString() })
          .eq('id', editingProduct.id);
        if (error) {
          console.error('Error actualizando producto:', error);
          throw new Error(`Error al actualizar: ${error.message}`);
        }

        if (stockNuevo !== editingProduct.stock) {
          const diff = stockNuevo - editingProduct.stock;
          const { error: kardexError } = await supabase.from('kardex').insert({
            producto_id: editingProduct.id,
            tipo: diff > 0 ? 'entrada' : 'salida',
            cantidad: Math.abs(diff),
            stock_anterior: editingProduct.stock,
            stock_nuevo: stockNuevo,
            motivo: 'Edición de producto',
            usuario_id: user?.id || null,
          });
          if (kardexError) console.error('Kardex error:', kardexError);
        }

        const { error: audError } = await supabase.from('auditoria').insert({
          usuario: user?.nombre || 'Sistema',
          accion: `Editó el producto: ${formData.nombre.trim()}`,
          modulo: 'Productos',
        });
        if (audError) console.error('Auditoría error:', audError);
        addToast('Producto actualizado correctamente', 'success');
      } else {
        const { data: newProd, error } = await supabase
          .from('productos')
          .insert(productoPayload)
          .select('id')
          .maybeSingle();
        if (error) {
          console.error('Error creando producto:', error);
          throw new Error(`Error al crear: ${error.message}`);
        }

        if (newProd && stockNuevo > 0) {
          const { error: kardexError } = await supabase.from('kardex').insert({
            producto_id: newProd.id,
            tipo: 'entrada',
            cantidad: stockNuevo,
            stock_anterior: 0,
            stock_nuevo: stockNuevo,
            motivo: 'Creación de producto',
            usuario_id: user?.id || null,
          });
          if (kardexError) console.error('Kardex error:', kardexError);
        }

        const { error: audError } = await supabase.from('auditoria').insert({
          usuario: user?.nombre || 'Sistema',
          accion: `Creó el producto: ${formData.nombre.trim()}`,
          modulo: 'Productos',
        });
        if (audError) console.error('Auditoría error:', audError);
        addToast('Producto creado correctamente', 'success');
      }

      onSaved();
      onClose();
    } catch (err: unknown) {
      let msg = 'Error al guardar el producto';
      if (err instanceof Error) msg = err.message;
      if (typeof err === 'object' && err && 'message' in err) msg = String((err as { message: unknown }).message);
      console.error('Producto error completo:', err);
      addToast(msg, 'error');
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  const inputClass = (field: string) =>
    `w-full px-4 py-2.5 bg-background-50 border rounded-lg text-foreground-800 text-sm focus:outline-none focus:ring-2 transition-all ${
      errors[field] ? 'border-accent-400 focus:ring-accent-100' : 'border-background-200 focus:border-primary-400 focus:ring-primary-100'
    }`;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 animate-fade-in" onClick={onClose} />
      <div className="relative bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto animate-scale-in">
        <div className="sticky top-0 bg-white z-10 flex items-center justify-between px-6 py-4 border-b border-background-200 rounded-t-2xl">
          <h2 className="font-heading text-lg font-semibold text-foreground-800">
            {editingProduct ? 'Editar Producto' : 'Nuevo Producto'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 rounded-lg text-foreground-400 hover:text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground-700 mb-1.5">Imagen</label>
            <input ref={fileInputRef} type="file" accept="image/*" onChange={handleImageChange} className="hidden" />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="w-full flex items-center justify-center gap-2 px-4 py-4 rounded-lg border-2 border-dashed border-background-300 text-foreground-400 hover:border-primary-300 hover:text-primary-500 transition-all duration-200 cursor-pointer"
            >
              {imagePreview ? (
                <img src={imagePreview} alt="Preview" className="max-h-32 rounded-lg object-contain" />
              ) : (
                <>
                  <i className="ri-image-add-line text-xl"></i>
                  <span className="text-sm">Subir imagen del producto</span>
                </>
              )}
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="prod-nombre" className="block text-sm font-medium text-foreground-700 mb-1.5">
                Nombre <span className="text-accent-500">*</span>
              </label>
              <input id="prod-nombre" type="text" value={formData.nombre} onChange={(e) => handleChange('nombre', e.target.value)}
                className={inputClass('nombre')} placeholder="Nombre del producto" />
              {errors.nombre && <p className="text-xs text-accent-500 mt-1">{errors.nombre}</p>}
            </div>
            <div>
              <label htmlFor="prod-tipo" className="block text-sm font-medium text-foreground-700 mb-1.5">
                Tipo de producto <span className="text-accent-500">*</span>
              </label>
              <input id="prod-tipo" type="text" value={formData.tipo_producto} onChange={(e) => handleChange('tipo_producto', e.target.value)}
                className={inputClass('tipo_producto')} placeholder="Ej: Analgésico, Jarabe, Crema..." />
              {errors.tipo_producto && <p className="text-xs text-accent-500 mt-1">{errors.tipo_producto}</p>}
            </div>
          </div>

          <div>
            <label htmlFor="prod-desc" className="block text-sm font-medium text-foreground-700 mb-1.5">Descripción</label>
            <textarea id="prod-desc" value={formData.descripcion} onChange={(e) => handleChange('descripcion', e.target.value)} rows={2}
              maxLength={500}
              className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-lg text-foreground-800 text-sm focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all resize-none"
              placeholder="Descripción del producto" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="prod-lab" className="block text-sm font-medium text-foreground-700 mb-1.5">Laboratorio</label>
              <input id="prod-lab" type="text" value={formData.laboratorio} onChange={(e) => handleChange('laboratorio', e.target.value)}
                className={inputClass('laboratorio')} placeholder="Ej: Bago, Inti" />
            </div>
            <div>
              <label htmlFor="prod-principio" className="block text-sm font-medium text-foreground-700 mb-1.5">Principio activo</label>
              <input id="prod-principio" type="text" value={formData.principio_activo} onChange={(e) => handleChange('principio_activo', e.target.value)}
                className={inputClass('principio_activo')} placeholder="Ej: Paracetamol" />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label htmlFor="prod-vencimiento" className="block text-sm font-medium text-foreground-700 mb-1.5">
                Fecha de vencimiento <span className="text-accent-500">*</span>
              </label>
              <input id="prod-vencimiento" type="date" value={formData.fecha_vencimiento} onChange={(e) => handleChange('fecha_vencimiento', e.target.value)}
                className={inputClass('fecha_vencimiento')} />
              {errors.fecha_vencimiento && <p className="text-xs text-accent-500 mt-1">{errors.fecha_vencimiento}</p>}
            </div>
            <div>
              <label htmlFor="prod-pcompra" className="block text-sm font-medium text-foreground-700 mb-1.5">Precio compra (Bs.)</label>
              <input id="prod-pcompra" type="number" step="0.01" min="0" value={formData.precio_compra} onChange={(e) => handleChange('precio_compra', e.target.value)}
                className={inputClass('precio_compra')} placeholder="0.00" />
              {errors.precio_compra && <p className="text-xs text-accent-500 mt-1">{errors.precio_compra}</p>}
            </div>
            <div>
              <label htmlFor="prod-precio" className="block text-sm font-medium text-foreground-700 mb-1.5">
                Precio venta (Bs.) <span className="text-accent-500">*</span>
              </label>
              <input id="prod-precio" type="number" step="0.01" min="0" value={formData.precio} onChange={(e) => handleChange('precio', e.target.value)}
                className={inputClass('precio')} placeholder="0.00" />
              {errors.precio && <p className="text-xs text-accent-500 mt-1">{errors.precio}</p>}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="prod-stock" className="block text-sm font-medium text-foreground-700 mb-1.5">
                Stock <span className="text-accent-500">*</span>
              </label>
              <input id="prod-stock" type="number" min="0" value={formData.stock} onChange={(e) => handleChange('stock', e.target.value)}
                className={inputClass('stock')} placeholder="0" />
              {errors.stock && <p className="text-xs text-accent-500 mt-1">{errors.stock}</p>}
            </div>
            <div>
              <label htmlFor="prod-stockmin" className="block text-sm font-medium text-foreground-700 mb-1.5">
                Stock mínimo <span className="text-accent-500">*</span>
              </label>
              <input id="prod-stockmin" type="number" min="0" value={formData.stock_minimo} onChange={(e) => handleChange('stock_minimo', e.target.value)}
                className={inputClass('stock_minimo')} placeholder="5" />
              {errors.stock_minimo && <p className="text-xs text-accent-500 mt-1">{errors.stock_minimo}</p>}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="prod-ubicacion" className="block text-sm font-medium text-foreground-700 mb-1.5">Ubicación</label>
              <input id="prod-ubicacion" type="text" value={formData.ubicacion} onChange={(e) => handleChange('ubicacion', e.target.value)}
                className={inputClass('ubicacion')} placeholder="Ej: Estante A-3" />
            </div>
            <div>
              <label htmlFor="prod-proveedor" className="block text-sm font-medium text-foreground-700 mb-1.5">Proveedor</label>
              <input id="prod-proveedor" type="text" value={formData.proveedor} onChange={(e) => handleChange('proveedor', e.target.value)}
                className={inputClass('proveedor')} placeholder="Ej: Laboratorios XYZ" />
            </div>
          </div>

          <div>
            <label htmlFor="prod-recibo" className="block text-sm font-medium text-foreground-700 mb-1.5">
              Número de recibo / factura <span className="text-accent-500">*</span>
            </label>
            <input id="prod-recibo" type="text" value={formData.numero_recibo} onChange={(e) => handleChange('numero_recibo', e.target.value)}
              className={inputClass('numero_recibo')} placeholder="Ej: REC-000125 / FAC-001245 / 123456" />
            {errors.numero_recibo && <p className="text-xs text-accent-500 mt-1">{errors.numero_recibo}</p>}
          </div>

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="flex-1 py-2.5 bg-background-100 hover:bg-background-200 text-foreground-700 font-medium rounded-lg transition-all duration-200 cursor-pointer whitespace-nowrap text-sm">
              Cancelar
            </button>
            <button type="submit" disabled={loading}
              className="flex-1 py-2.5 bg-primary-500 hover:bg-primary-600 disabled:bg-primary-300 text-white font-medium rounded-lg transition-all duration-200 cursor-pointer whitespace-nowrap text-sm flex items-center justify-center gap-2">
              {loading ? (<><i className="ri-loader-4-line animate-spin"></i><span>Guardando...</span></>) : (<><i className="ri-save-line"></i><span>{editingProduct ? 'Actualizar' : 'Crear Producto'}</span></>)}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}